import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import {WEB_FAQ_EDIT_SHORT} from "../../../../../../constants/appUrls.constants";
import {isDefined} from "../../../../../../utils";
import faqStyles from "./faqStyles";
import {sortByLanguageCode} from "./faqLanguageOrder";
import DeleteFaq from "./DeleteFaq";

// Surukle-birak ile siralanabilen SSS karti. Surukleme yalnizca tutamactan baslar;
// karta tiklamak cevabi acar, dil rozetleri o dildeki metni gosterir.
const SortableFaqItem = ({faq, position, defaultLanguageCode, propertyWebId}) => {
    const {t} = useTranslation();
    const classes = faqStyles();

    const [expanded, setExpanded] = useState(false);
    const [activeCode, setActiveCode] = useState(defaultLanguageCode);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: faq?.faq_group_id});

    const languages = sortByLanguageCode(faq?.languages, lang => lang?.language_code);

    useEffect(() => {
        setActiveCode(defaultLanguageCode);
    }, [defaultLanguageCode]);

    const active = languages.find(lang => lang?.language_code === activeCode) || languages[0];
    const question = active?.question;
    const answer = active?.answer;
    const isPublished = Number(faq?.status) === 1;

    return (
        <div
            ref={setNodeRef}
            className={`${classes.item} ${isDragging ? classes.itemDragging : ''}`}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.9 : 1,
                zIndex: isDragging ? 20 : undefined,
                position: 'relative',
            }}
        >
            <Box className={classes.itemRow}>
                <Tooltip title={t('enw-web-faq-drag_to_sort', 'Sıralamak için sürükleyin')} placement="top">
                    <span className={classes.dragHandle} {...attributes} {...listeners}>
                        <i className="fas fa-arrows"/>
                    </span>
                </Tooltip>

                {/* Sira rozeti listedeki konumdan uretilir; backend ayni order_number'i
                    birden fazla gruba verebiliyor (ilk surukle-birak sonrasi duzeliyor). */}
                <Box className={classes.orderBadge}>{position}</Box>

                <Box className={classes.itemBody} onClick={() => setExpanded(prev => !prev)}>
                    <Typography className={`${classes.question} ${isDefined(question) ? '' : classes.questionEmpty}`}>
                        {isDefined(question) ? question : t('enw-web-faq-no_question', 'Soru girilmemiş')}
                    </Typography>

                    {!expanded && isDefined(answer) &&
                        <Typography className={classes.answerPreview}>{answer}</Typography>
                    }

                    <Box className={classes.langChipRow} onClick={(event) => event.stopPropagation()}>
                        {languages.map(lang => (
                            <span
                                key={lang?.language_code}
                                className={`${classes.langChip} ${lang?.language_code === active?.language_code ? classes.langChipActive : ''}`}
                                onClick={() => {
                                    setActiveCode(lang?.language_code);
                                    setExpanded(true);
                                }}
                            >
                                <img
                                    className={classes.langChipFlag}
                                    src={`${process.env.PUBLIC_URL}/assets/img/language/${lang?.language_code || ''}.svg`}
                                    alt={lang?.language_code || ''}
                                />
                                {(lang?.language_code || '').toUpperCase()}
                            </span>
                        ))}
                    </Box>
                </Box>

                <Chip
                    size="small"
                    className={`${classes.statusChip} ${isPublished ? classes.statusPublish : classes.statusPreview}`}
                    label={isPublished ? t('enw-web-faq-status_publish', 'Yayında') : t('enw-web-faq-status_preview', 'Önizleme')}
                />

                <Box className={classes.actions}>
                    <Tooltip title={expanded ? t('btn-close') : t('enw-web-faq-show_answer', 'Cevabı göster')} placement="top">
                        <IconButton className={classes.iconBtn} onClick={() => setExpanded(prev => !prev)}>
                            <i className={`fas fa-chevron-down ${classes.expandIcon} ${expanded ? classes.expandIconOpen : ''}`}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('btn-edit')} placement="top">
                        <IconButton
                            className={classes.iconBtn}
                            component={Link}
                            to={`${WEB_FAQ_EDIT_SHORT}/${faq?.faq_group_id}`}
                        >
                            <i className="fas fa-edit"/>
                        </IconButton>
                    </Tooltip>
                    <DeleteFaq faq={faq} question={question} propertyWebId={propertyWebId}/>
                </Box>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Typography className={classes.answerFull} component="div">
                    {isDefined(answer) ? answer : (
                        <Fragment>{t('enw-web-faq-no_answer', 'Bu dilde cevap girilmemiş.')}</Fragment>
                    )}
                </Typography>
            </Collapse>
        </div>
    );
};

SortableFaqItem.propTypes = {
    faq: PropTypes.object.isRequired,
    position: PropTypes.number,
    defaultLanguageCode: PropTypes.string,
    propertyWebId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SortableFaqItem;
